/**
 * CSS preview styles per transition type (approximates export look in the editor).
 */
import { normalizeTransitionOut } from '@/lib/videoTransitions';

function pct(v) {
  return `${Math.round(v * 10000) / 100}%`;
}

/** Merge motion/filter/transition CSS layers for the preview player. */
export function mergePreviewLayerStyles(...layers) {
  const transforms = [];
  const filters = [];
  let opacity;
  let transformOrigin = 'center center';
  let willChange;
  for (const layer of layers) {
    if (!layer || typeof layer !== 'object') continue;
    if (layer.transform) transforms.push(layer.transform);
    if (layer.filter) filters.push(layer.filter);
    if (layer.opacity != null) opacity = layer.opacity;
    if (layer.transformOrigin) transformOrigin = layer.transformOrigin;
    if (layer.willChange) willChange = layer.willChange;
  }
  const out = { transformOrigin };
  if (willChange) out.willChange = willChange;
  if (transforms.length) out.transform = transforms.join(' ');
  if (filters.length) out.filter = filters.join(' ');
  if (opacity != null) out.opacity = opacity;
  return out;
}

/** Apply merged preview CSS to a media element without a React re-render. */
export function applyPreviewLayerStylesToElement(el, styles) {
  if (!el || !styles || typeof styles !== 'object') return;
  if (styles.transform != null) el.style.transform = styles.transform;
  else el.style.removeProperty('transform');
  if (styles.filter != null) el.style.filter = styles.filter;
  else el.style.removeProperty('filter');
  if (styles.opacity != null) el.style.opacity = String(styles.opacity);
  else el.style.removeProperty('opacity');
  if (styles.transformOrigin != null) el.style.transformOrigin = styles.transformOrigin;
  if (styles.willChange != null) el.style.willChange = styles.willChange;
  else el.style.removeProperty('will-change');
}

/** @returns {{ outgoing: object, incoming: object } | null} */
export function transitionPreviewLayerStyles(transitionId, progress) {
  const id = normalizeTransitionOut(transitionId);
  const p = Math.max(0, Math.min(1, Number(progress) || 0));
  if (id === 'cut') return null;

  const base = { transformOrigin: 'center center', willChange: 'transform, opacity, filter' };

  if (id === 'cross_dissolve' || id === 'slow_cross_dissolve' || id === 'morph_cut' || id === 'match_cut') {
    const dissolveP = id === 'slow_cross_dissolve' ? p * p : p;
    return {
      outgoing: { ...base, opacity: 1 - dissolveP },
      incoming: { ...base, opacity: dissolveP },
    };
  }

  if (id === 'fade_in' || id === 'fade_out' || id === 'dip_to_black' || id === 'dip_to_white') {
    const mid = p < 0.5 ? p * 2 : (p - 0.5) * 2;
    return {
      outgoing: { ...base, opacity: p < 0.5 ? 1 - mid : 0 },
      incoming: { ...base, opacity: p < 0.5 ? 0 : mid },
    };
  }

  if (id === 'push_right' || id === 'slide_left' || id === 'swipe_left' || id === 'hand_swipe') {
    return {
      outgoing: { ...base, transform: `translateX(${pct(-p)})` },
      incoming: { ...base, transform: `translateX(${pct(1 - p)})` },
    };
  }

  if (id === 'push_left' || id === 'slide_right' || id === 'swipe_right' || id === 'page_turn') {
    return {
      outgoing: { ...base, transform: `translateX(${pct(p)})` },
      incoming: { ...base, transform: `translateX(${pct(-1 + p)})` },
    };
  }

  if (id === 'whip_pan' || id === 'motion_blur' || id === 'zoom_blur') {
    const blur = Math.max(0, 12 - Math.abs(p - 0.5) * 24);
    const shift = (p - 0.5) * 40;
    return {
      outgoing: {
        ...base,
        opacity: p < 0.55 ? 1 : 0,
        transform: `translateX(${pct(-p * 0.35)})`,
        filter: `blur(${blur}px)`,
      },
      incoming: {
        ...base,
        opacity: p > 0.45 ? 1 : 0,
        transform: `translateX(${pct((1 - p) * 0.35)})`,
        filter: `blur(${blur}px)`,
      },
    };
  }

  if (id === 'zoom_in' || id === 'spin_zoom' || id === 'doorway_travel') {
    const scaleOut = 1 + p * 0.35;
    const scaleIn = 0.8 + p * 0.2;
    return {
      outgoing: { ...base, transform: `scale(${scaleOut})`, opacity: p < 0.6 ? 1 : 1 - (p - 0.6) / 0.4 },
      incoming: { ...base, transform: `scale(${scaleIn})`, opacity: p < 0.4 ? 0 : (p - 0.4) / 0.6 },
    };
  }

  if (id === 'zoom_out') {
    const scaleOut = 1 + (1 - p) * 0.2;
    return {
      outgoing: { ...base, transform: `scale(${scaleOut})`, opacity: 1 - p },
      incoming: { ...base, transform: `scale(${0.9 + p * 0.1})`, opacity: p },
    };
  }

  if (id === 'spin') {
    return {
      outgoing: { ...base, transform: `rotate(${p * 90}deg) scale(1.05)`, opacity: p < 0.5 ? 1 : 0 },
      incoming: { ...base, transform: `rotate(${(p - 0.5) * -90}deg)`, opacity: p > 0.5 ? 1 : 0 },
    };
  }

  if (id === 'drone_rise' || id === 'ancient_map_reveal') {
    return {
      outgoing: { ...base, transform: `translateY(${pct(-p)})` },
      incoming: { ...base, transform: `translateY(${pct(1 - p)})` },
    };
  }

  if (id === 'glitch' || id === 'rgb_split') {
    return {
      outgoing: { ...base, opacity: 1 - p, transform: `translateX(${pct((p - 0.5) * 0.04)})` },
      incoming: {
        ...base,
        opacity: p,
        filter: `hue-rotate(${Math.round(p * 40)}deg)`,
        transform: `translateX(${pct((0.5 - p) * 0.04)})`,
      },
    };
  }

  if (id === 'flash' || id === 'film_burn' || id === 'light_leak' || id === 'lens_flare') {
    return {
      outgoing: { ...base, opacity: p < 0.5 ? 1 - p * 2 : 0, filter: p < 0.5 ? `brightness(${1 + p})` : undefined },
      incoming: { ...base, opacity: p < 0.5 ? 0 : (p - 0.5) * 2, filter: `brightness(${1 + (1 - p) * 0.3})` },
    };
  }

  // Default dissolve fallback
  return {
    outgoing: { ...base, opacity: 1 - p },
    incoming: { ...base, opacity: p },
  };
}
